import { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import Button from '@mui/material/Button';
import ImageViewer from './ImageViewer';
import { getCanvasImgData } from '../utils/api';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Checkbox from '@mui/material/Checkbox';
import { createReviewedKernal, getReviewedKernal } from '../utils/api'

const ImageComponent = ({
  index,
  imageUrl,
  kernelDetails,
  classNameCard,
  imageUrlForBottom,
  ledImageUrl,
  ledImageUrlForBottom,
  processData,
  scanDetails,
  scanId,
  subScanId,
  handleCheckboxClick,
  boundingBox,
  selectedIndices,
  kernals,
  setKernals,
  rotatedTopImageUrl
}) => {
  const userID = localStorage.getItem('user_id')
  const [changedClassName, setChangeClassName] = useState(classNameCard);
  const [open, setOpen] = useState(false);
  const [multipleBoxesUrl, setMultipleBoxesUrl] = useState('')
  const [multipleBoxesUrlForBottom, setMultipleBoxesUrlForBottom] = useState('')
  const [ledMultipleBoxesUrl, setLedMultipleBoxesUrl] = useState('');
  const [ledMultipleBoxesUrlForBottom, setLedMultipleBoxesUrlForBottom] = useState('');
  const [isLoading, setIsLoading] = useState(false)

  const handleCheckBox = async (className, index) => {
    await handleCheckboxClick(className, index)
  };

  const searchInLocalStorage = useCallback(
    (index, originalClassName) => {
      const allChanges = kernals;

      if (!Array.isArray(allChanges)) {
        return { changed: false, finalObj: {} };
      }

      const filteredChanges = allChanges.filter(
        (change) =>
          change.scan_id === scanId &&
          change.sub_scan_id === subScanId &&
          change.review_index === index &&
          change.original_class_name === originalClassName
      );

      if (filteredChanges.length === 0) {
        return { changed: false, finalObj: {} };
      }

      const latestObj = filteredChanges.reduce((prev, curr) => {
        const prevDate = moment(prev.created_at, 'DD MMM, YYYY hh:mm A');
        const currDate = moment(curr.created_at, 'DD MMM, YYYY hh:mm A');
        return currDate.isAfter(prevDate) ? curr : prev;
      });

      return {
        changed: true,
        finalObj: latestObj,
      };
    },
    [kernals, scanId, subScanId]
  );

  useEffect(() => {
    const { changed, finalObj } = searchInLocalStorage(index, classNameCard);
    if (changed) {
      setChangeClassName(finalObj.changed_class_name);
    }
  }, [index, classNameCard, searchInLocalStorage]);

  const handleButtonSelect = (className) => {
    setChangeClassName(className);
    handleChangeClassName(className, index, classNameCard);
  };

  const handleChangeClassName = async (newClassName, index, classNameCard) => {
    if (processData?.bboxes && processData?.bboxes.hasOwnProperty(classNameCard)) {
      const bboxes = processData.bboxes[classNameCard];
      const bboxesBottom = processData?.bboxes_bottom?.[classNameCard];
      if (bboxes && bboxes.length > index) {
        const [x, y, w, h] = bboxes[index];
        const reviewed_kernels = {
          original_class_name: classNameCard,
          changed_class_name: newClassName,
          scan_id: scanId,
          sub_scan_id: subScanId,
          review_index: index,
          coordinates: [x, y, w, h],
          bottom_coordinates:
            bboxesBottom && bboxesBottom.length > index
              ? bboxesBottom[index]
              : null,
          user_id: userID,
        };
        await createReviewedKernal(reviewed_kernels)
        const payload = {
          scan_id: scanId,
          sub_scan_id: subScanId
        };
        const kernalsResponse = await getReviewedKernal(payload)
        setKernals(kernalsResponse?.data?.data)
      } else {
        console.error(`Invalid review index ${index} for className ${newClassName}`);
      }
    }
  };

  const showBoundingBoxes = async (className, index) => {
    setOpen(true);
    setIsLoading(true);

    const files = scanDetails?.scan_files || [];

    // Normal top
    const capturedTopFile = files.find(f => {
      const name = f.file_name.toLowerCase();
      return name.includes('captured') &&
        !name.includes('bottom') &&
        !name.includes('led');
    }) || files.find(f => {
      const name = f.file_name.toLowerCase();
      return name.includes('_cv_server') &&
        !name.includes('bottom') &&
        !name.includes('led');
    });
    const capturedTopUrl = rotatedTopImageUrl || capturedTopFile?.file_url || null;

    // Normal bottom
    const capturedBottomFile = files.find(f => f.file_name.includes('captured_bottom')) ||
      files.find(f => f.file_name.includes('_bottom_cv_server'));

    const capturedBottomUrl = capturedBottomFile?.file_url || null;

    // LED top
    const ledTopFile = files.find(f => f.file_name?.includes('captured_led'));
    const ledTopUrl = ledTopFile?.file_url || null;

    // LED bottom
    const ledBottomFile = files.find(f => f.file_name?.includes('captured_bottom_led'));
    const ledBottomUrl = ledBottomFile?.file_url || null;

    // Bounding boxes
    const boundingBox = getBoundingBoxByClassName(className);
    const boundingBoxForBottom = getBoundingBoxForBottomByClassName(className);

    const selectedBoundingBox = boundingBox?.[index];
    const selectedBoundingBoxForBottom = boundingBoxForBottom?.[index];

    let boxesUrl, boxesUrlForBottom, ledBoxesUrl, ledBoxesUrlForBottom;

    // Normal top
    if (selectedBoundingBox && capturedTopUrl) {
      boxesUrl = await getCanvasImgData(capturedTopUrl, JSON.stringify(selectedBoundingBox));
    }

    // Normal bottom
    if (selectedBoundingBoxForBottom && capturedBottomUrl) {
      boxesUrlForBottom = await getCanvasImgData(capturedBottomUrl, JSON.stringify(selectedBoundingBoxForBottom));
    }

    // LED top
    if (selectedBoundingBox && ledTopUrl) {
      ledBoxesUrl = await getCanvasImgData(ledTopUrl, JSON.stringify(selectedBoundingBox));
    }

    // LED bottom
    if (selectedBoundingBoxForBottom && ledBottomUrl) {
      ledBoxesUrlForBottom = await getCanvasImgData(ledBottomUrl, JSON.stringify(selectedBoundingBoxForBottom));
    }

    // Set state
    setMultipleBoxesUrl(boxesUrl);
    setMultipleBoxesUrlForBottom(boxesUrlForBottom);
    setLedMultipleBoxesUrl(ledBoxesUrl);
    setLedMultipleBoxesUrlForBottom(ledBoxesUrlForBottom);

    setIsLoading(false);
  };

  const getBoundingBoxByClassName = (className) => {
    if (processData?.bboxes && processData?.bboxes.hasOwnProperty(className)) {
      return processData?.bboxes[className];
    }
    return null;
  };
  const getBoundingBoxForBottomByClassName = (className) => {
    if (processData?.bboxes_bottom && processData?.bboxes_bottom.hasOwnProperty(className)) {
      return processData?.bboxes_bottom[className];
    }
    return null;
  };
  const handleCloseModal = () => {
    setOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          width: imageUrlForBottom ? "400px" : "200px",
          height: "auto",
          border:
            classNameCard === changedClassName
              ? "1px solid black"
              : "4px solid red",
          position: "relative",
          display: imageUrlForBottom ? "flex" : "block",
          flexWrap: "wrap",
          paddingBottom: "40px", // Add space for the absolute positioned elements
        }}
        key={index}
      >
        {/* ===== NORMAL TOP ===== */}
        <div style={{ width: imageUrlForBottom ? "50%" : "100%", height: "200px" }}>
          <img
            src={imageUrl}
            alt={`Cropped ${index + 1}`}
            style={{ maxHeight: "80%", maxWidth: "80%" }}
          />
          {imageUrlForBottom && <div style={{ fontSize: 14 }}>TOP</div>}
        </div>

        {/* ===== NORMAL BOTTOM ===== */}
        {imageUrlForBottom && (
          <div style={{ width: "50%", height: "200px" }}>
            <img
              src={imageUrlForBottom}
              alt={`Cropped ${index + 1}`}
              style={{ maxHeight: "80%", maxWidth: "80%" }}
            />
            <div style={{ fontSize: 14 }}>BOTTOM</div>
          </div>
        )}

        {/* ===== LED TOP ===== */}
        {ledImageUrl &&
          <div style={{ width: imageUrlForBottom ? "50%" : "100%", height: "200px" }}>
            <img
              src={ledImageUrl}
              alt={`LED Cropped ${index + 1}`}
              style={{ maxHeight: "80%", maxWidth: "80%" }}
            />
            <div style={{ fontSize: 14 }}>TOP BOTTOM LED</div>
          </div>}

        {/* ===== LED BOTTOM ===== */}
        {ledImageUrlForBottom && (
          <div style={{ width: "50%", height: "200px" }}>
            <img
              src={ledImageUrlForBottom}
              alt={`LED Cropped ${index + 1}`}
              style={{ maxHeight: "80%", maxWidth: "80%" }}
            />
            <div style={{ fontSize: 14 }}>BOTTOM TOP LED</div>
          </div>
        )}

        {/* ===== ARROW ICON (BOTTOM LEFT) ===== */}
        <div
          className="icon-container"
          onClick={() => showBoundingBoxes(classNameCard, index)}
          style={{
            position: "absolute",
            bottom: "8px",
            left: "8px",
            cursor: "pointer",
          }}
        >
          <ArrowForwardIcon />
        </div>

        {/* ===== CHECKBOX (BOTTOM RIGHT) ===== */}
        <div
          className="checkbox-container"
          style={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
          }}
        >
          <Checkbox
            checked={selectedIndices.includes(boundingBox[index])}
            onChange={() => handleCheckBox(classNameCard, index)}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${imageUrlForBottom ? 3 : 2}, 1fr)`, gap: '10px', padding: '20px' }}>
        {kernelDetails?.classes &&
          kernelDetails.classes.map((className, buttonIndex) => (
            <Button
              key={className}
              variant="outlined"
              style={{
                color: 'black',
                border:
                  changedClassName === className
                    ? '3px solid green'
                    : '1px solid #1976d2',
              }}
              onClick={() => handleButtonSelect(className)}
            >
              {` ${processData?.encodings[className]}`}
            </Button>
          ))}

        <Button variant="outlined"
          style={{
            color: 'black',
            border:
              changedClassName === 'Cant_Determine'
                ? '3px solid green'
                : '1px solid #1976d2',
          }}
          onClick={() => handleButtonSelect('Cant_Determine')}>
          CD
        </Button>
        {open && (
          <ImageViewer
            open={open}
            isLoading={isLoading}
            onClose={handleCloseModal}
            title="Image Viewer"
            imageUrl={multipleBoxesUrl}
            imageUrlForBottom={multipleBoxesUrlForBottom}
            ledMultipleBoxesUrl={ledMultipleBoxesUrl}
            ledMultipleBoxesUrlForBottom={ledMultipleBoxesUrlForBottom}
            imageAlt={'captured Image'}
            imageAltForBottom={'Bottom captured Image'}
          />
        )}
      </div>
    </div>
  );
};

export default ImageComponent;

